import Component from '@glimmer/component';
import { service } from '@ember/service';
import { concat, uniqueId } from '@ember/helper';
import { trackedFunction } from 'reactiveweb/function';
import Chip from 'tio-ui/components/chip';
import Table from 'tio-ui/components/table';
import { reportScheduleRunsQuery } from 'tio-data/requests/report-schedule-run';
import { groupRunsBySchedule } from '../utils/scheduled-export-grouping';
import type { Store as StoreService } from '@warp-drive/core';
import type { ReportScheduleRun } from 'tio-data/data/resources/report-schedule-run.type';

interface ScheduledExportsListSignature {
  Args: {
    /** Company whose scheduled-export runs to list. */
    companyId: string;
    /**
     * Render one table per schedule under its report name, instead of a single
     * flat run list. The partner view uses this — a partner reads their own
     * schedules ("my weekly TAS export, and how it has been doing"), while an
     * admin reads recent export activity across the whole company.
     */
    groupBySchedule?: boolean;
  };
  Element: HTMLDivElement;
}

/** Single-page pull of a company's scheduled-export runs (server caps page size at 400). */
const RUN_PAGE_LIMIT = 400;

interface RunsTableSignature {
  Args: {
    runs: ReportScheduleRun[];
    isLoading?: boolean;
    /**
     * Show the per-run Report column. Off when grouped, where the report name is
     * already the group heading and repeating it in every row is noise.
     */
    showReport: boolean;
    /**
     * Grouped mode only: the schedule these runs belong to. Present, it names
     * this table uniquely for tests — the flat layout's bare
     * `data-test-scheduled-exports-rows` would otherwise repeat once per group.
     */
    scheduleId?: string;
    /**
     * Grouped mode only: id of the `<h3>` naming this table. Without it a
     * screen reader announces several indistinguishable "table" landmarks,
     * since a preceding heading is not programmatically associated.
     */
    labelledBy?: string;
  };
}

/**
 * The run table itself, shared by the flat and grouped layouts so the row markup
 * (and its a11y wiring) has exactly one definition.
 */
class RunsTable extends Component<RunsTableSignature> {
  formatRunAt = (iso: string) =>
    new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso)
    );

  <template>
    <Table
      @isLoading={{@isLoading}}
      @previewColspan={{if @showReport 5 4}}
      @surface={{true}}
      aria-labelledby={{@labelledBy}}
      data-test-scheduled-exports-rows={{unless @scheduleId ""}}
      data-test-scheduled-export-rows-for={{@scheduleId}}
    >
      <:thead as |o|>
        <o.tr>
          {{#if @showReport}}
            <o.th class="text-left">Report</o.th>
          {{/if}}
          <o.th class="text-left">Ran at</o.th>
          <o.th class="text-left">Status</o.th>
          <o.th class="text-right">Rows</o.th>
          <o.th class="text-right">Download</o.th>
        </o.tr>
      </:thead>
      <:tbody as |o|>
        {{#each @runs as |run|}}
          <o.tr data-test-scheduled-export-row={{run.id}}>
            {{#if @showReport}}
              <o.td class="tio-fg-primary">{{run.reportName}}</o.td>
            {{/if}}
            <o.td class="tio-fg-muted">{{this.formatRunAt run.ranAt}}</o.td>
            <o.td>
              <Chip @size="sm" @intent={{if (eq run.status "succeeded") "success" "danger"}}>
                {{run.status}}
              </Chip>
            </o.td>
            <o.td class="text-right">
              {{#if (neq run.rowCount null)}}
                {{run.rowCount}}{{#if run.truncated}}
                  <span class="tio-fg-warning-body">(truncated)</span>
                {{/if}}
              {{else}}
                <span class="tio-fg-faint">—</span>
              {{/if}}
            </o.td>
            <o.td class="text-right">
              {{#if run.downloadUrl}}
                <a
                  href={{run.downloadUrl}}
                  class="tio-fg-info hover:tio-fg-info-h"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={{concat "Download export for " run.reportName}}
                  data-test-scheduled-export-download={{run.id}}
                >Download</a>
              {{else}}
                <span class="tio-fg-faint">—</span>
              {{/if}}
            </o.td>
          </o.tr>
        {{/each}}
      </:tbody>
    </Table>
  </template>
}

/**
 * Read-only scheduled-export history (RFC 080); manual exports create no ReportScheduleRun. The
 * host provides page chrome.
 * Grouping uses reportScheduleId on already-fetched runs and performs no schedule writes.
 * Successful exports retain download links even when summary email delivery fails.
 */
export default class ScheduledExportsList extends Component<ScheduledExportsListSignature> {
  @service declare store: StoreService;

  runsData = trackedFunction(this, async () => {
    if (!this.args.companyId) return [] as ReportScheduleRun[];
    const { content } = (await this.store.request(
      reportScheduleRunsQuery({
        'filter[company_id]': this.args.companyId,
        // Sort server-side so page[limit] keeps the NEWEST runs, not an arbitrary
        // slice — a client-only sort can't recover runs that were never fetched.
        sort: '-ran-at',
        'page[limit]': RUN_PAGE_LIMIT,
      })
    )) as { content: { data: ReportScheduleRun[] } };
    // Defensive re-sort in case the endpoint ignores the sort param.
    return [...content.data].sort((a, b) => Date.parse(b.ranAt) - Date.parse(a.ranAt));
  });

  get rows() {
    return this.runsData.value ?? [];
  }

  /** Schedules newest-activity-first, since `rows` is already newest-run-first. */
  get groups() {
    return groupRunsBySchedule(this.rows);
  }

  /**
   * Checks the collection used by the current layout so unattributable grouped runs yield an empty
   * message.
   */
  get isEmpty() {
    return this.args.groupBySchedule ? this.groups.length === 0 : this.rows.length === 0;
  }

  <template>
    <div class="tio-col-tight" data-test-scheduled-exports-list ...attributes>
      {{#if this.runsData.isError}}
        <p class="text-sm tio-fg-danger-body">Couldn't load scheduled exports.</p>
      {{else if (and (not this.runsData.isLoading) this.isEmpty)}}
        <p class="text-sm tio-fg-muted">No scheduled exports have run yet.</p>
      {{else if (and @groupBySchedule (not this.runsData.isLoading))}}
        {{#each this.groups as |group|}}
          {{#let (uniqueId) as |headingId|}}
            <section
              class="tio-col-tight"
              data-test-scheduled-export-group={{group.reportScheduleId}}
            >
              <h3
                id={{headingId}}
                class="tio-text-sm-semibold tio-fg-primary"
              >{{group.reportName}}</h3>
              <RunsTable
                @runs={{group.runs}}
                @showReport={{false}}
                @scheduleId={{group.reportScheduleId}}
                @labelledBy={{headingId}}
              />
            </section>
          {{/let}}
        {{/each}}
      {{else}}
        {{! Loading renders the flat skeleton in both modes — there are no groups
            to head yet, and the table already owns the loading treatment. }}
        <RunsTable
          @runs={{this.rows}}
          @isLoading={{this.runsData.isLoading}}
          @showReport={{true}}
        />
      {{/if}}
    </div>
  </template>
}
